import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { ArrowLeft } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCurrency = (val) => {
  const n = parseFloat(val) || 0;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const PIE_COLORS = ['#5B8DB8', '#FFA94D', '#808080', '#FFD700', '#4ECFDB', '#DC3545', '#8DB600', '#6C7B8A', '#FF8C00', '#9966CC'];

const CommissionReport = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) { setLoading(false); return; }

    Promise.all([
      fetch(`${API_URL}/commissions/${projectId}`).then(readResponseJSON),
      fetch(`${API_URL}/projects/${projectId}`).then(readResponseJSON),
    ])
      .then(([commData, projData]) => {
        if (!commData.error) setReport(commData);
        if (!projData.error) setProject(projData);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500">Loading report...</div>
      </Layout>
    );
  }

  if (!report || !projectId) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-400">No commission report available.</div>
      </Layout>
    );
  }

  const laborTotalHours = report.labor_overview.reduce((s, t) => s + t.hours, 0);
  const laborTotalCost = report.labor_overview.reduce((s, t) => s + t.total, 0);
  const laborTotalVehicle = report.labor_overview.reduce((s, t) => s + t.vehicle_expense, 0);
  const laborTotalFuel = report.labor_overview.reduce((s, t) => s + t.fuel_expense, 0);

  const pieData = report.labor_overview
    .filter(t => t.hours > 0)
    .map(t => ({ name: t.tech, value: t.hours }));

  const address = project?.job_location || report.location || '';
  const fullAddress = project?.address || report.address || '';
  const startDate = project?.start_date || report.start_date || 'n/a';
  const finishDate = project?.finish_date || report.finished_date || 'n/a';
  const paidInFull = project?.paid || report.paid_in_full || 'No';

  const netCommission = report.gross_commission - report.mgmt_expenses - report.labor_fuel_vehicle;

  return (
    <Layout>
      <div className="p-4 lg:p-8">
        <div className="bg-white rounded shadow p-6 lg:p-10" data-testid="commission-report">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#5B8DB8] hover:text-[#4a7ca5] mb-3 transition-colors" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <h1 className="text-3xl text-gray-800 font-light italic mb-3" data-testid="report-title">Commission Report</h1>
              <p className="text-lg font-bold text-gray-800">
                {report.customer} <span className="font-normal text-gray-500">[{report.project_id}]</span>
              </p>
              <p className="text-gray-600">{address}</p>
              <p className="text-gray-600">{fullAddress}</p>
            </div>
            <div className="text-right flex-shrink-0" data-testid="report-logo">
              <span className="text-3xl font-light text-gray-400">resto </span>
              <span className="inline-flex items-center justify-center w-8 h-8 bg-[#FFA94D] text-white text-xs font-bold rounded-full align-middle">mp</span>
            </div>
          </div>

          {/* Top Section: Dates | Commission Review | Project Expenses */}
          <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr_1fr] gap-6 mb-10">
            {/* Left - Dates */}
            <div className="space-y-4 pt-2">
              <div>
                <p className="text-sm font-bold text-gray-800">Project Started:</p>
                <p className="text-sm text-gray-600">{startDate}</p>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">Project Completed:</p>
                <p className="text-sm text-gray-600">{finishDate}</p>
              </div>
              <div>
                <p className="text-sm font-bold text-gray-800">Paid in Full:</p>
                <p className="text-sm text-gray-600">{paidInFull}</p>
              </div>
            </div>

            {/* Center - Commission Review */}
            <div>
              <h2 className="text-2xl text-gray-700 font-light italic mb-4" data-testid="commission-review-title">Commission Review</h2>
              <table className="w-full text-sm" data-testid="commission-review-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1"></th>
                    <th className="text-right py-1 text-gray-500 text-xs font-normal">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 font-bold text-gray-800">Project Value</td>
                    <td className="py-2 text-right font-bold">{fmtCurrency(report.subtotal)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">Expenses</td>
                    <td className="py-2 text-right text-gray-600">{fmtCurrency(report.non_mgmt_expenses)}</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="py-2 font-bold text-gray-800">Net Value</td>
                    <td className="py-2 text-right font-bold">{fmtCurrency(report.commissionable_total)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">Commission %</td>
                    <td className="py-2 text-right text-gray-600">{report.commission_percent}%</td>
                  </tr>
                  <tr className="border-b bg-gray-50">
                    <td className="py-2 font-bold text-gray-800">Gross Commission</td>
                    <td className="py-2 text-right font-bold">{fmtCurrency(report.gross_commission)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">Truck Rental</td>
                    <td className="py-2 text-right text-gray-600">{fmtCurrency(report.vehicle_total)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">Fuel Expense</td>
                    <td className="py-2 text-right text-gray-600">{fmtCurrency(report.fuel_total)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">Labor Expense</td>
                    <td className="py-2 text-right text-gray-600">{fmtCurrency(report.labor_cost)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-2 font-bold text-gray-800">Net Commission</td>
                    <td className="py-2 text-right font-bold">{fmtCurrency(netCommission)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Right - Project Expenses */}
            <div>
              <h2 className="text-2xl text-gray-700 font-light italic mb-4" data-testid="project-expenses-title">Project Expenses</h2>
              {report.expenses_breakdown.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No expenses recorded</p>
              ) : (
                <table className="w-full text-sm" data-testid="project-expenses-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 text-gray-500 text-xs font-normal">Type</th>
                      <th className="text-right py-1 text-gray-500 text-xs font-normal">Expense</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.expenses_breakdown.map((exp, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2 text-gray-700">{exp.type || 'Other'}</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(exp.expense)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50">
                      <td className="py-2"></td>
                      <td className="py-2 text-right font-medium">{fmtCurrency(report.non_mgmt_expenses)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Middle Section: Commission Advances | Commission Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            {/* Commission Advances */}
            <div>
              <h2 className="text-2xl text-gray-700 font-light italic mb-4" data-testid="advances-title">Commission Advances</h2>
              {report.advance_entries.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No advances recorded</p>
              ) : (
                <table className="w-full text-sm" data-testid="advances-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 text-gray-500 text-xs font-normal">Employee</th>
                      <th className="text-left py-1 text-gray-500 text-xs font-normal">Date</th>
                      <th className="text-right py-1 text-gray-500 text-xs font-normal">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.advance_entries.map((adv, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2 text-gray-700">{adv.employee}</td>
                        <td className="py-2 text-gray-700">{adv.date}</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(adv.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="py-2"></td>
                      <td className="py-2"></td>
                      <td className="py-2 text-right font-medium">{fmtCurrency(report.advances)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* Commission Distribution */}
            <div>
              <h2 className="text-2xl text-gray-700 font-light italic mb-4" data-testid="distribution-title">Commission Distribution</h2>
              <table className="w-full text-sm" data-testid="distribution-table">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-1 text-gray-500 text-xs font-normal">Production Manager</th>
                    <th className="text-left py-1 text-gray-500 text-xs font-normal">% Given</th>
                    <th className="text-right py-1 text-gray-500 text-xs font-normal">Commission</th>
                    <th className="text-right py-1 text-gray-500 text-xs font-normal">Draws</th>
                    <th className="text-right py-1 text-gray-500 text-xs font-normal">Available Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {report.production_manager ? (
                    <>
                      <tr className="border-b">
                        <td className="py-2 text-gray-700">{report.production_manager}</td>
                        <td className="py-2 text-gray-700">100%</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(netCommission)}</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(report.advances)}</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(netCommission - report.advances)}</td>
                      </tr>
                      <tr>
                        <td className="py-2"></td>
                        <td className="py-2"></td>
                        <td className="py-2 text-right font-medium">{fmtCurrency(netCommission)}</td>
                        <td className="py-2 text-right font-medium">{fmtCurrency(report.advances)}</td>
                        <td className="py-2 text-right font-medium">{fmtCurrency(netCommission - report.advances)}</td>
                      </tr>
                    </>
                  ) : (
                    <tr><td colSpan="5" className="py-2 text-gray-500 italic">No production manager assigned</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Section: Labor Overview | Hourly Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Labor Overview */}
            <div>
              <h2 className="text-2xl text-gray-700 font-light italic mb-4" data-testid="labor-title">Labor Overview</h2>
              {report.labor_overview.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No labor entries</p>
              ) : (
                <table className="w-full text-sm" data-testid="labor-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1 text-gray-500 text-xs font-normal">Technician</th>
                      <th className="text-right py-1 text-gray-500 text-xs font-normal">Hours</th>
                      <th className="text-right py-1 text-gray-500 text-xs font-normal">Total</th>
                      <th className="text-right py-1 text-gray-500 text-xs font-normal">Vehicle Expense</th>
                      <th className="text-right py-1 text-gray-500 text-xs font-normal">Fuel Expense</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.labor_overview.map((tech, i) => (
                      <tr key={i} className={`border-b ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="py-2 text-gray-700">{tech.tech}</td>
                        <td className="py-2 text-right text-gray-700">{tech.hours.toFixed(1)}</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(tech.total)}</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(tech.vehicle_expense)}</td>
                        <td className="py-2 text-right text-gray-700">{fmtCurrency(tech.fuel_expense)}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-100">
                      <td className="py-2 font-medium"></td>
                      <td className="py-2 text-right font-medium">{laborTotalHours.toFixed(2)}</td>
                      <td className="py-2 text-right font-medium">{fmtCurrency(laborTotalCost)}</td>
                      <td className="py-2 text-right font-medium">{fmtCurrency(laborTotalVehicle)}</td>
                      <td className="py-2 text-right font-medium">{fmtCurrency(laborTotalFuel)}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>

            {/* Hourly Summary Pie Chart */}
            <div>
              <h2 className="text-2xl text-gray-700 font-light italic mb-4" data-testid="hourly-summary-title">Hourly Summary</h2>
              {pieData.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No labor data for chart</p>
              ) : (
                <div style={{ width: '100%', height: 320 }} data-testid="hourly-summary-chart">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={110}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={true}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CommissionReport;
